import { Link } from "react-router-dom";
import type { Article } from "@/mocks/types";
import { formatViews, formatDate } from "@/lib/utils";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 items-start"
      >
        <div className="relative w-24 h-24 md:w-32 md:h-24 shrink-0 overflow-hidden rounded-lg">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {article.isVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-foreground-900/40">
              <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-play-fill text-background-50 text-sm" />
              </div>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
            <span>{formatDate(article.date)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block"
      >
        <div className="relative aspect-square overflow-hidden rounded-lg mb-3">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {article.isVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-foreground-900/30">
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-play-fill text-background-50" />
              </div>
            </div>
          )}
          {article.isHot && (
            <div className="absolute top-2 left-2 px-2 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded">
              热门
            </div>
          )}
        </div>
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
          <span>{article.category}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all hover:shadow-sm"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {article.isVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-foreground-900/30">
            <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-play-fill text-background-50 text-lg" />
            </div>
          </div>
        )}
        {article.isHot && (
          <div className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded">
            热门爆料
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
            {article.category}
          </span>
          <span className="text-xs text-foreground-500">{formatDate(article.date)}</span>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-100">
          <span className="text-xs text-foreground-500">{article.author}</span>
          <span className="text-xs text-foreground-500 flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}